import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ToastProvider } from './toast/ToastContext';
import { DrawerProvider } from './drawer/DrawerContext';
import { ENTRA_LOGOUT_URL } from '../config/api';
import SearchableSelect from './SearchableSelect';

/* Shared shell for every portal in this app (ICIB admin, employer, employee):
   the navy rail on the left, a topbar, and the routed view in between.
   Each portal supplies its own nav map and labels; the chrome and design
   tokens (src/styles/theme.css) stay identical across all three so they
   read as one product. Also mounts the shared toast + drawer systems so
   any view under this portal can call useToast()/useDrawer(). */
function PortalLayout({ navSections, brandRole, userLabel, userInitials, showGroupScope = false, groupScope = null, loading = false }) {
  // On phone widths the rail becomes an off-canvas menu (see theme.css);
  // close it whenever the route changes so a nav tap reveals the view.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  function signOut() {
    // A real navigation, not a fetch: GET /auth/logout clears the local
    // session then 302s out to Entra's own end-session endpoint and back
    // (RP-initiated logout, BACKEND_API_FLOW.md §3.3) — an XHR can't
    // follow that redirect chain, only the browser can.
    window.location.href = ENTRA_LOGOUT_URL;
  }

  return (
    <ToastProvider>
      <DrawerProvider>
        <div className="app-shell">
          <div className={'rail-scrim' + (navOpen ? ' open' : '')} onClick={() => setNavOpen(false)} aria-hidden="true" />
          <nav id="portal-rail" className={'rail' + (navOpen ? ' open' : '')} aria-label="Sections">
            <div className="brand">
              <div className="mark">ICIB Brokerweb</div>
              <h1>Employee Benefits</h1>
              <div className="role">{brandRole}</div>
            </div>

            {navSections.map((g) => (
              <div className="navgroup" key={g.section}>
                <span>{g.section}</span>
                {g.items.map((item) => (
                  <NavLink
                    key={item.path || 'overview'}
                    to={item.path}
                    end={item.path === ''}
                    className={({ isActive }) => 'navitem' + (isActive ? ' active' : '')}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}

            <div className="foot">Employee Benefits Portal<br />ICIB Brokerweb</div>
          </nav>

          <div className="main-col">
            <header className="topbar">
              {loading && <div className="topbar-loading" aria-hidden="true" />}
              <button
                type="button"
                className="menu-btn"
                aria-label="Open navigation"
                aria-controls="portal-rail"
                aria-expanded={navOpen}
                onClick={() => setNavOpen((o) => !o)}
              >
                <span /><span /><span />
              </button>
              {groupScope ? (
                <div className="scope">
                  <label htmlFor="scope">Company</label>
                  <SearchableSelect
                    id="scope"
                    className="scope-picker"
                    value={groupScope.value}
                    onChange={groupScope.onChange}
                    options={groupScope.options}
                    disabled={groupScope.disabled}
                    title={groupScope.disabled ? 'This section is not company-scoped' : undefined}
                    placeholder="Search company or code…"
                  />
                  {groupScope.tag && !groupScope.disabled && <span className="tag navy">{groupScope.tag}</span>}
                </div>
              ) : (
                showGroupScope && (
                  <div className="scope">
                    <label htmlFor="scope">Company</label>
                    <select id="scope" className="scope-select" disabled title="Wire up once /companies is connected">
                      <option>All companies</option>
                    </select>
                  </div>
                )
              )}
              <div className="who">
                <span>{userLabel}</span>
                <span className="av">{userInitials}</span>
                <button className="btn sm" onClick={signOut}>Sign out</button>
              </div>
            </header>

            <main className="view">
              <Outlet />
            </main>
          </div>
        </div>
      </DrawerProvider>
    </ToastProvider>
  );
}

export default PortalLayout;
